import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';

import Popup from 'components/Popup/Popup';
import { useTickets } from 'contexts/TicketsContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import type { NoopT } from 'declarations/types/common';

import { getPopupContentStyle } from './ColumnPickerPopup.styled';
import ColumnPickerSettings from './components/ColumnPickerSettings/ColumnPickerSettings';
import useColumnPickerSettingsPopup from './hooks/useColumPickerSettingsPopup';

type ColumnPickerSettingsPopupPropsT = {
  isOpen?: boolean;
  onClose?: NoopT;
};

const ColumnPickerSettingsPopup: FC<ColumnPickerSettingsPopupPropsT> = ({ isOpen, onClose }) => {
  const { handleCloseColumnPickerPopup, isPopupOpened } = useColumnPickerSettingsPopup();
  const { activeTab } = useTickets();

  return (
    <Popup
      showCloseBtn
      modalTitle={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Customize Columns</Typography>
          <Tooltip
            title={
              <Box>
                <Box display="flex" alignItems="flex-start" gap={0.5} mb={0.5}>
                  <Typography variant="caption">&bull;</Typography>
                  <Typography variant="caption">
                    Drag and drop columns to adjust their order.
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <Typography variant="caption">&bull;</Typography>
                  <Typography variant="caption">
                    Use the switch to hide and show columns.
                  </Typography>
                </Box>
              </Box>
            }
            arrow
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: '1.25rem',
                color: (theme): string => theme.palette.text.secondary,
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
      }
      popupId={ScrollLockIdE.ColumnPickerPopup}
      contentStyle={getPopupContentStyle()}
      isOpen={isPopupOpened || !!isOpen}
      onClose={handleCloseColumnPickerPopup(onClose)}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      size={
        activeTab === ActiveTabTypeE.Schedule
          ? ModalSizeE.COLUMN_PICKER_SCHEDULE
          : ModalSizeE.COLUMN_PICKER
      }
      data-testid="column-picker-popup"
    >
      <ColumnPickerSettings
        activeTab={activeTab}
        onSaveAndClose={handleCloseColumnPickerPopup(undefined)}
      />
    </Popup>
  );
};

export default ColumnPickerSettingsPopup;
